import { useState } from 'react';
import { Link } from 'react-router-dom';

interface ProductSpec {
  potencia?: string;
  velocidade?: string;
  temperatura?: string;
  material?: string;
  [key: string]: string | undefined;
}

interface ProductCardProps {
  id: string;
  name: string;
  badge: string | null;
  image: string;
  specs: ProductSpec;
  href: string;
}

export default function ProductCard({ name, badge, image, specs, href }: ProductCardProps) {
  const specEntries = [
    { icon: 'ri-flashlight-line', label: 'Potência', value: specs.potencia },
    { icon: 'ri-speed-line', label: 'Velocidade', value: specs.velocidade },
    { icon: 'ri-temp-hot-line', label: 'Temperatura', value: specs.temperatura },
    { icon: 'ri-stack-line', label: 'Material', value: specs.material },
  ].filter((s) => s.value);
  const [zoomed, setZoomed] = useState(false);

  return (
    <>
      <div className="group bg-white rounded-2xl overflow-hidden border border-gray-100 flex flex-col transition-all duration-300 hover:-translate-y-2 hover:border-green-200">
        <div className="relative w-full h-52 bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-6">
          {badge && (
            <span className="absolute top-3 left-3 bg-green-500 text-white text-xs font-black px-2.5 py-1 rounded-full uppercase tracking-wide">
              {badge}
            </span>
          )}
          <button
            className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-white/80 hover:bg-white rounded-full transition-all cursor-pointer opacity-0 group-hover:opacity-100 z-10"
            aria-label="Ampliar imagem"
            onClick={() => setZoomed(true)}
          >
            <i className="ri-zoom-in-line text-gray-700 text-sm" />
          </button>
          <img
            alt={`Máquina de solda geomembrana ${name} para impermeabilização profissional`}
            className="h-full w-full object-contain group-hover:scale-105 transition-transform duration-500 cursor-zoom-in"
            src={image}
            loading="lazy"
            decoding="async"
            onClick={() => setZoomed(true)}
          />
        </div>

        <div className="p-5 flex flex-col flex-1">
          <h3 className="text-2xl font-black text-gray-900 mb-4 uppercase">{name}</h3>

          <div className="grid grid-cols-2 gap-2 mb-5">
            {specEntries.map((s) => (
              <div key={s.label} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i className={`${s.icon} text-green-500 text-sm`} />
                </div>
                <div className="min-w-0">
                  <p className="text-gray-900 text-[11px] uppercase tracking-wide leading-none mb-0.5">{s.label}</p>
                  <p className="text-gray-900 text-[13px] truncate">{s.value}</p>
                </div>
              </div>
            ))}
          </div>

          <Link
            to={href}
            className="mt-auto flex items-center justify-center gap-2 border-2 border-green-500 text-green-600 hover:bg-green-500 hover:text-white text-sm px-5 py-2.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap uppercase tracking-wide"
          >
            Ver Informações
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>

      {zoomed && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-6 cursor-zoom-out"
          onClick={() => setZoomed(false)}
        >
          <img
            src={image}
            alt={`Máquina de solda geomembrana ${name}`}
            className="max-w-full max-h-full object-contain rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center bg-white/20 hover:bg-white/40 rounded-full text-white transition-all cursor-pointer"
            onClick={() => setZoomed(false)}
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>
      )}
    </>
  );
}
